import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { fmt, calcInvoiceTotals } from '../utils/gst';
import toast from 'react-hot-toast';
import ReportView from '../components/ReportView';
import WorkReportView from '../components/WorkReportView';
import { getInitialMonth } from '../utils/format';
import SearchableSelect from '../components/SearchableSelect';
import PasswordField from '../components/PasswordField';

export default function ReportPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(getInitialMonth);
  const [clientFilter, setClientFilter] = useState('');
  const [useRange, setUseRange] = useState(false);
  const [fromMonth, setFromMonth] = useState(month);
  const [toMonth, setToMonth] = useState(month);
  const [shareModal, setShareModal] = useState(false); // false | { nodeId?: string, personName?: string }
  const [shareLink, setShareLink] = useState('');
  const [shareExpiry, setShareExpiry] = useState('30');
  const [sharePassword, setSharePassword] = useState('');
  const [previewNode, setPreviewNode] = useState(false); // false | { nodeId?: string, personName?: string }
  const [wrPreview, setWrPreview] = useState(false); // false | true — Work Report (pricing) preview open?
  const [wrModal, setWrModal] = useState(false);
  const [wrLink, setWrLink] = useState('');
  const [wrExpiry, setWrExpiry] = useState('30');
  const [wrPassword, setWrPassword] = useState('');
  const [exporting, setExporting] = useState(false);
  const [linksModal, setLinksModal] = useState(false);
  const [pwEditId, setPwEditId] = useState(null);
  const [pwEditValue, setPwEditValue] = useState('');

  useEffect(()=>{const h=e=>setMonth(e.detail);window.addEventListener('monthChange',h);return()=>window.removeEventListener('monthChange',h);},[]);
  useEffect(()=>{ if(!useRange){ setFromMonth(month); setToMonth(month); } },[month]);

  // Preview overlays (👁 View / 👁 View Work Report) push a history entry when opened, so the browser's
  // Back button closes the overlay and stays on this page, instead of leaving /report entirely.
  useEffect(() => {
    const onPopState = () => { setPreviewNode(false); setWrPreview(false); };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  function openPreviewNode(node) { window.history.pushState({ reportOverlay: true }, ''); setPreviewNode(node); }
  function openWrPreview() { window.history.pushState({ reportOverlay: true }, ''); setWrPreview(true); }
  function closeOverlay() { window.history.back(); }

  const { data, isLoading } = useQuery({
    queryKey: useRange ? ['report','range',fromMonth,toMonth,clientFilter] : ['report',month,clientFilter],
    queryFn:()=>api.get(useRange
      ? `/reports/monthly?fromMonth=${fromMonth}&toMonth=${toMonth}${clientFilter?'&clientId='+clientFilter:''}`
      : `/reports/monthly?month=${month}${clientFilter?'&clientId='+clientFilter:''}`
    ).then(r=>r.data),
  });
  const { data: clients=[] } = useQuery({ queryKey:['clients'], queryFn:()=>api.get('/clients').then(r=>r.data) });

  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey:['shared-preview',useRange?`${fromMonth}_${toMonth}`:month,clientFilter,previewNode?.nodeId],
    // Follow the period actually on screen: a From/To range must not silently fall back to the top-bar month.
    queryFn:()=>api.get(`/reports/shared-preview?${useRange?`fromMonth=${fromMonth}&toMonth=${toMonth}`:`month=${month}`}${clientFilter?'&clientId='+clientFilter:''}${previewNode?.nodeId?'&nodeId='+previewNode.nodeId:''}`).then(r=>r.data),
    enabled: !!previewNode,
  });

  const { data: wrPreviewData, isLoading: wrPreviewLoading } = useQuery({
    queryKey: useRange ? ['work-report-preview','range',fromMonth,toMonth,clientFilter] : ['work-report-preview',month,clientFilter],
    queryFn:()=>api.get(useRange
      ? `/reports/work-report-preview?fromMonth=${fromMonth}&toMonth=${toMonth}&clientId=${clientFilter}`
      : `/reports/work-report-preview?month=${month}&clientId=${clientFilter}`
    ).then(r=>r.data),
    enabled: !!wrPreview && !!clientFilter,
  });

  const shareMutation = useMutation({
    mutationFn:()=>{
      // Person links are single-month: use the month on screen, never the top-bar month behind a range.
      if (useRange && fromMonth!==toMonth) return Promise.reject(new Error('Person links cover one month at a time — set From and To to the same month first.'));
      return api.post('/reports/share',{
      month: useRange ? fromMonth : month,
      clientId:clientFilter||undefined,
      nodeId: shareModal?.nodeId || undefined,
      expiresInDays:parseInt(shareExpiry),
      password:sharePassword||undefined,
    });},
    onSuccess:res=>{ setShareLink(res.data.url); },
    onError:(err)=>toast.error(err?.message?.startsWith('Person links') ? err.message : 'Failed to generate link'),
  });

  const wrMutation = useMutation({
    mutationFn:()=>api.post('/reports/work-report-share',{
      ...(useRange ? {fromMonth,toMonth} : {month}),
      clientId: clientFilter||undefined,
      expiresInDays: parseInt(wrExpiry),
      password: wrPassword||undefined,
    }),
    onSuccess:res=>{ setWrLink(res.data.url); },
    onError:(err)=>toast.error(err?.response?.data?.error||'Failed to generate link'),
  });

  const { data: links=[] } = useQuery({
    queryKey: ['report-links', clientFilter],
    queryFn: () => api.get('/reports/links', { params: { clientId: clientFilter } }).then(r => r.data),
    enabled: linksModal && !!clientFilter,
  });

  const setLinkPasswordMutation = useMutation({
    mutationFn: ({ id, password }) => api.patch(`/reports/links/${id}/password`, { password }),
    onSuccess: () => { toast.success('Password updated'); qc.invalidateQueries(['report-links']); setPwEditId(null); setPwEditValue(''); },
    onError: () => toast.error('Failed to update password'),
  });

  const revokeLinkMutation = useMutation({
    mutationFn: id => api.delete(`/reports/links/${id}`),
    onSuccess: () => { toast.success('Link revoked'); qc.invalidateQueries(['report-links']); },
    onError: () => toast.error('Failed to revoke link'),
  });

  if(isLoading) return <div className="loading-screen"><div className="spinner"/></div>;
  const d=data||{};

  // Report template logic: summary | summary_person | full
  function shouldShowPerson(template) { return template==='summary_person'||template==='full'; }
  function shouldShowStakeholder(template) { return template==='full'; }

  return (
    <div>
      <div className="section-head">
        <h2>Monthly report — {useRange
          ? `${new Date(fromMonth+'-01').toLocaleString('default',{month:'long',year:'numeric'})} – ${new Date(toMonth+'-01').toLocaleString('default',{month:'long',year:'numeric'})}`
          : new Date(month+'-01').toLocaleString('default',{month:'long',year:'numeric'})}</h2>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <div style={{width:180}}>
            <SearchableSelect value={clientFilter} onChange={setClientFilter} items={clients}
              getLabel={c=>`${c.name}${c.report_template!=='full'?` (${c.report_template})`:''}`}
              allowClear clearLabel="All clients" placeholder="All clients" />
          </div>
          <button onClick={()=>{setShareLink('');setShareModal({});}} style={{fontSize:12}} title={useRange?'Share link works for a single month — turn off month range first':''} disabled={useRange}>🔗 Share link</button>
          <button className="btn-primary btn-sm" onClick={openWrPreview} style={{fontSize:12}} title={!clientFilter?'Select a client first':''} disabled={!clientFilter}>👁 View Work Report</button>
          <button onClick={()=>{setWrLink('');setWrModal(true);}} style={{fontSize:12}} title={!clientFilter?'Select a client first':''} disabled={!clientFilter}>🔗 Share Work Report</button>
          <button onClick={()=>setLinksModal(true)} style={{fontSize:12}} title={!clientFilter?'Select a client first':''} disabled={!clientFilter}>📋 Manage links</button>
          <button className="btn-primary btn-sm" disabled={exporting} onClick={async()=>{
            setExporting(true);
            try{const r=await api.get(useRange
              ? `/reports/export?fromMonth=${fromMonth}&toMonth=${toMonth}${clientFilter?'&clientId='+clientFilter:''}`
              : `/reports/export?month=${month}${clientFilter?'&clientId='+clientFilter:''}`,{responseType:'blob'});
            const url=URL.createObjectURL(r.data);const a=document.createElement('a');a.href=url;a.download=`8D_Business_OS_${useRange?`${fromMonth}_to_${toMonth}`:month}.xlsx`;a.click();setTimeout(()=>URL.revokeObjectURL(url),5000);
            toast.success('Excel exported');}
            catch{toast.error('Export failed');}
            finally{setExporting(false);}
          }}>{exporting ? '⏳ Exporting…' : '⬇ Export Excel'}</button>
        </div>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <button className="btn-sm" onClick={()=>setUseRange(v=>!v)} style={{background:useRange?'var(--accent-light)':'',color:useRange?'var(--accent)':'',fontWeight:useRange?600:400}}>
          📅 {useRange?'Using month range':'Use month range'}
        </button>
        {useRange && <>
          <span style={{fontSize:12,color:'var(--text2)'}}>From</span>
          <input type="month" className="form-input" value={fromMonth} onChange={e=>setFromMonth(e.target.value)} style={{width:140}}/>
          <span style={{fontSize:12,color:'var(--text2)'}}>To</span>
          <input type="month" className="form-input" value={toMonth} onChange={e=>setToMonth(e.target.value)} style={{width:140}}/>
        </>}
      </div>

      {(d.report||[]).map(({client,totals,nodeMap,totalJobs})=>{
        const template = client.report_template||'full';
        return (
          <div key={client.id} className="card" style={{marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:8}}>
              <div>
                <div style={{fontWeight:700,fontSize:15}}>{client.name}</div>
                <div style={{fontSize:11,color:'var(--text2)'}}>{client.state} · {totals.taxType==='intrastate'?'CGST+SGST':'IGST'} · {totalJobs} jobs
                  {template!=='full'&&<span className="pill pill-orange" style={{marginLeft:6,fontSize:9}}>{template}</span>}
                </div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:11,color:'var(--text2)'}}>Grand total (incl. GST)</div>
                <div style={{fontSize:18,fontWeight:800,color:'var(--accent)'}}>{fmt(totals.grandTotal)}</div>
              </div>
            </div>

            {/* Summary — always shown */}
            {Object.entries(nodeMap).map(([pk,pd])=>{
              if(pk!=='__none__') return null;
              return Object.entries(pd.entries).map(([nk,nd])=>(
                <div key={nk} style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:8}}>
                  {nd.items.map(e=>(
                    <span key={e.id} className={`pill ${e.corrections>0?'pill-red':'pill-gray'}`} style={{fontSize:11}}>
                      {e.category_name} ×{e.quantity}{e.corrections>0?<span className="cor-tag" style={{marginLeft:4}}>+{e.corrections}</span>:''}
                    </span>
                  ))}
                </div>
              ));
            })}

            {/* Person level */}
            {shouldShowPerson(template) && Object.entries(nodeMap).map(([pk,pd])=>{
              if(pk==='__none__') return null;
              return (
                <details key={pk} className="person-block" open>
                  <summary className="person-block-head">
                    <span>{pd.label}: {pk} <span style={{fontWeight:400,fontSize:11,color:'var(--text2)'}}>{Object.values(pd.entries).reduce((s,n)=>s+n.items.reduce((s2,e)=>s2+e.quantity,0),0)} jobs</span></span>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      {pd.nodeId&&<button className="btn-sm" style={{fontSize:10,padding:'2px 8px'}} onClick={ev=>{ev.preventDefault();openPreviewNode({nodeId:pd.nodeId,personName:pk});}}>👁 View</button>}
                      {pd.nodeId&&<button className="btn-sm" style={{fontSize:10,padding:'2px 8px'}} onClick={ev=>{ev.preventDefault();setShareLink('');setShareModal({nodeId:pd.nodeId,personName:pk});}}>🔗 Share for {pk}</button>}
                      <span style={{fontWeight:700}}>{fmt(pd.base+pd.corrAmt)}</span>
                    </div>
                  </summary>
                  <div className="person-block-body">
                    {/* Stakeholder level */}
                    {shouldShowStakeholder(template) && Object.entries(pd.entries).map(([nk,nd])=>(
                      nk!=='__none__'&&<div key={nk} className="sh-block">
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                          <div><span style={{width:7,height:7,borderRadius:'50%',background:'var(--accent)',display:'inline-block',marginRight:6}}></span><strong>{nk}</strong> <span style={{fontSize:11,color:'var(--text2)'}}>{nd.items.reduce((s,e)=>s+e.quantity,0)} jobs</span></div>
                          <span style={{fontWeight:700,fontSize:12}}>{fmt(nd.base+nd.corrAmt)}</span>
                        </div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                          {nd.items.map(e=>(
                            <span key={e.id} className={`pill ${e.corrections>0?'pill-red':'pill-gray'}`} style={{fontSize:10}}>
                              {e.category_name} ×{e.quantity}{e.corrections>0?<span className="cor-tag" style={{marginLeft:3}}>+{e.corrections}{e.correction_reason?`: ${e.correction_reason.slice(0,20)}`:''}</span>:''}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              );
            })}

            <hr className="divider"/>
            <div style={{maxWidth:280,marginLeft:'auto',fontSize:13}}>
              <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0'}}><span>Subtotal</span><span>{fmt(totals.subtotal)}</span></div>
              {totals.taxType==='intrastate'
                ?<><div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',color:'var(--text2)'}}><span>CGST</span><span>{fmt(totals.cgst)}</span></div>
                    <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',color:'var(--text2)'}}><span>SGST</span><span>{fmt(totals.sgst)}</span></div></>
                :<div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',color:'var(--text2)'}}><span>IGST</span><span>{fmt(totals.igst)}</span></div>}
              <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',fontWeight:800,fontSize:15,borderTop:'2px solid var(--border)',marginTop:4}}>
                <span>Grand Total</span><span style={{color:'var(--accent)'}}>{fmt(totals.grandTotal)}</span>
              </div>
            </div>
          </div>
        );
      })}
      {!d.report?.length&&<div className="empty">No data for this period</div>}

      {wrModal&&(
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Work Report link</div>
            {!wrLink&&<>
              <p style={{fontSize:13,color:'var(--text2)',marginBottom:14}}>
                Generates a client-facing work report with images, designer breakdown, and GST summary. Pricing is visible.
              </p>
              <div className="field"><label>Expiry</label>
                <select value={wrExpiry} onChange={e=>setWrExpiry(e.target.value)}>
                  <option value="3">3 days</option><option value="7">7 days</option><option value="30">30 days</option>
                </select>
              </div>
              <div className="field"><label>Password (optional)</label>
                <PasswordField value={wrPassword} onChange={e=>setWrPassword(e.target.value)} placeholder="Leave blank for no password"/>
              </div>
              <div className="modal-actions">
                <button onClick={()=>{setWrModal(false);setWrPassword('');}}>Cancel</button>
                <button className="btn-primary" onClick={()=>wrMutation.mutate()} disabled={wrMutation.isPending}>{wrMutation.isPending?'Generating…':'Generate link'}</button>
              </div>
            </>}
            {wrLink&&<>
              <div style={{fontSize:12,color:'var(--green)',marginBottom:8}}>✓ Work report link generated — expires in {wrExpiry} days{wrPassword?' · Password protected':''}</div>
              <input value={wrLink} readOnly onClick={e=>e.target.select()} style={{marginBottom:12}}/>
              <div className="modal-actions">
                <button onClick={()=>{navigator.clipboard.writeText(wrLink);toast.success('Copied!');}}>Copy link</button>
                <button className="btn-primary" onClick={()=>{setWrModal(false);setWrLink('');setWrPassword('');}}>Done</button>
              </div>
            </>}
          </div>
        </div>
      )}

      {linksModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-title">Active share links</div>
            <p style={{fontSize:12,color:'var(--text2)',marginBottom:14}}>
              Links for <strong>{clients.find(c=>c.id===clientFilter)?.name}</strong> that haven't expired yet.
              The original password can't be shown again for security — use "Change password" to put a new one on the same link without breaking it for the client.
            </p>
            {links.length===0 && <div style={{fontSize:13,color:'var(--text3)',padding:'20px 0',textAlign:'center'}}>No active links for this client.</div>}
            {links.map(l=>{
              const daysLeft = Math.max(0, Math.ceil((new Date(l.expires_at)-new Date())/86400000));
              const rangeLabel = l.to_month ? `${l.month?.trim()} – ${l.to_month.trim()}` : l.month?.trim();
              return (
                <div key={l.id} style={{border:'1px solid var(--border)',borderRadius:8,padding:'10px 12px',marginBottom:8}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                    <div style={{fontSize:12,fontWeight:600}}>
                      {l.report_type==='work_report' ? '💰 Work Report (pricing)' : '📄 Report'}
                      {l.node_name && <span style={{color:'var(--text2)',fontWeight:400}}> · {l.node_name}</span>}
                    </div>
                    <span style={{fontSize:11,color:daysLeft<=3?'var(--red)':'var(--text3)'}}>{daysLeft===0?'Expires today':`Expires in ${daysLeft}d`}</span>
                  </div>
                  <div style={{fontSize:11,color:'var(--text3)',marginBottom:8}}>
                    {rangeLabel}
                    {l.password_protected && <span style={{marginLeft:8,color:'var(--accent)'}}>🔒 Password protected</span>}
                  </div>
                  <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                    <input value={l.url} readOnly onClick={e=>e.target.select()} style={{flex:1,minWidth:180,fontSize:11,padding:'4px 8px'}}/>
                    <button className="btn-sm" onClick={()=>{navigator.clipboard.writeText(l.url);toast.success('Link copied!');}}>Copy link</button>
                    <button className="btn-sm" onClick={()=>{setPwEditId(pwEditId===l.id?null:l.id);setPwEditValue('');}}>{l.password_protected?'Change password':'Add password'}</button>
                    <button className="btn-danger btn-sm" onClick={()=>{ if(window.confirm('Revoke this link? The client will no longer be able to open it.')) revokeLinkMutation.mutate(l.id); }}>Revoke</button>
                  </div>
                  {pwEditId===l.id && (
                    <div style={{display:'flex',gap:6,marginTop:8,alignItems:'center'}}>
                      <PasswordField value={pwEditValue} onChange={e=>setPwEditValue(e.target.value)} placeholder="New password (blank = remove password)" style={{flex:1}} autoFocus/>
                      <button className="btn-primary btn-sm" disabled={setLinkPasswordMutation.isPending}
                        onClick={()=>setLinkPasswordMutation.mutate({id:l.id,password:pwEditValue})}>Save</button>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="modal-actions">
              <button className="btn-primary" onClick={()=>{setLinksModal(false);setPwEditId(null);}}>Close</button>
            </div>
          </div>
        </div>
      )}

      {shareModal&&(
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">
              {shareModal.personName ? `Share — ${shareModal.personName}` : 'Share report link'}
            </div>
            {!shareLink&&<>
              {shareModal.personName
                ? <div style={{fontSize:12,padding:'8px 10px',background:'var(--accent-light)',borderRadius:'var(--radius-sm)',marginBottom:12,color:'var(--accent)'}}>
                    Scoped link — client will see only <strong>{shareModal.personName}</strong>'s entries. No pricing shown.
                  </div>
                : <p style={{fontSize:13,color:'var(--text2)',marginBottom:14}}>Generates a read-only link for the client. No pricing shown.</p>
              }
              <div className="field"><label>Expiry</label>
                <select value={shareExpiry} onChange={e=>setShareExpiry(e.target.value)}>
                  <option value="3">3 days</option><option value="7">7 days</option><option value="30">30 days</option>
                </select>
              </div>
              <div className="field"><label>Password (optional)</label>
                <PasswordField value={sharePassword} onChange={e=>setSharePassword(e.target.value)} placeholder="Leave blank for no password"/>
              </div>
              <div className="modal-actions">
                <button onClick={()=>setShareModal(null)}>Cancel</button>
                <button className="btn-primary" onClick={()=>shareMutation.mutate()} disabled={shareMutation.isPending}>{shareMutation.isPending?'Generating…':'Generate link'}</button>
              </div>
            </>}
            {shareLink&&<>
              <div style={{fontSize:12,color:'var(--green)',marginBottom:8}}>✓ Link generated — expires in {shareExpiry} days{sharePassword?' · Password protected':''}{shareModal?.personName?` · Scoped to ${shareModal.personName}`:''}</div>
              <input value={shareLink} readOnly onClick={e=>e.target.select()} style={{marginBottom:12}}/>
              <div className="modal-actions">
                <button onClick={()=>{navigator.clipboard.writeText(shareLink);toast.success('Copied!');}}>Copy link</button>
                <button className="btn-primary" onClick={()=>{setShareModal(null);setShareLink('');setSharePassword('');}}>Done</button>
              </div>
            </>}
          </div>
        </div>
      )}

      {previewNode&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:9999,display:'flex',flexDirection:'column'}}>
          <div style={{background:'#fff',padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid var(--border)'}}>
            <strong>Preview — what {previewNode.personName} will see</strong>
            <div style={{display:'flex',gap:8}}>
              <button className="btn-primary btn-sm" onClick={()=>{const n=previewNode;closeOverlay();setShareLink('');setShareModal({nodeId:n.nodeId,personName:n.personName});}}>🔗 Share for {previewNode.personName}</button>
              <button className="btn-ghost btn-sm" onClick={closeOverlay}>✕ Close</button>
            </div>
          </div>
          <div style={{flex:1,overflow:'auto',background:'var(--surface2)'}}>
            {previewLoading
              ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}><div className="spinner"/></div>
              : <ReportView data={previewData} />}
          </div>
        </div>
      )}

      {wrPreview&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:9999,display:'flex',flexDirection:'column'}}>
          <div style={{background:'#fff',padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid var(--border)'}}>
            <strong>Preview — Work Report (pricing included)</strong>
            <div style={{display:'flex',gap:8}}>
              <button className="btn-primary btn-sm" onClick={()=>{closeOverlay();setWrLink('');setWrModal(true);}}>🔗 Share Work Report</button>
              <button className="btn-ghost btn-sm" onClick={closeOverlay}>✕ Close</button>
            </div>
          </div>
          <div style={{flex:1,overflow:'auto',background:'var(--surface2)'}}>
            {wrPreviewLoading
              ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}><div className="spinner"/></div>
              : <WorkReportView data={wrPreviewData} />}
          </div>
        </div>
      )}
    </div>
  );
}
